'use client'

import React from 'react'
import { Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAlkanes } from '../lib/store'

/// A ghost bar — the width a figure will occupy, unmistakably not a figure.
function Bar({ className }: { className?: string }) {
	return <span className={cn('block h-3 rounded bg-secondary', className)} aria-hidden="true" />
}

function GhostAvatar({ size = 44 }: { size?: number }) {
	return (
		<span
			aria-hidden="true"
			style={{ width: size, height: size }}
			className="block shrink-0 rounded-full bg-secondary"
		/>
	)
}

/// Medal for ranks 1-3, a plain number after. Drawn as coloured roundels so
/// the ranked-row anatomy reads at a glance even while the rows are empty.
function RankMark({ n }: { n: number }) {
	if (n <= 3) {
		const tone =
			n === 1
				? 'bg-warning/25 text-warning-ink'
				: n === 2
				? 'bg-secondary text-muted-foreground'
				: 'bg-destructive/20 text-destructive-ink'
		return (
			<span
				className={cn(
					'grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold tabular-nums',
					tone
				)}
			>
				{n}
			</span>
		)
	}
	return (
		<span className="w-6 shrink-0 text-center text-sm tabular-nums text-muted-foreground">
			{n}.
		</span>
	)
}

/// The leaderboard, in fomo's anatomy — and honestly empty. No global
/// per-trader PnL exists for alkanes today: trades are pseudonymous outpoints,
/// and nothing on-chain ties a fill to a person. Rather than invent ranked
/// traders, the screen ships the structure (tabs, your-rank card, window and
/// unit toggles, medal rows) with its emptiness stated in plain text, so the
/// day a ranking service exists the surface is already designed.
export default function Leaderboard() {
	const loggedIn = useAlkanes((s) => s.loggedIn)
	const [tab, setTab] = React.useState<'friends' | 'leaderboard'>('leaderboard')
	const [window_, setWindow] = React.useState<'24h' | '7d' | '30d'>('24h')
	const [unit, setUnit] = React.useState<'%' | '$'>('$')

	return (
		<section aria-label="Leaderboard" data-testid="alkanes-leaderboard" className="flex flex-col">
			<div role="tablist" aria-label="Leaderboard tabs" className="flex border-b border-border">
				{(
					[
						['friends', 'Friends'],
						['leaderboard', 'Leaderboard']
					] as const
				).map(([id, label]) => {
					const on = tab === id
					return (
						<button
							key={id}
							type="button"
							role="tab"
							aria-selected={on}
							data-testid={`alkanes-leaderboard-tab-${id}`}
							onClick={() => setTab(id)}
							className={cn(
								'relative flex-1 py-3 text-[15px] font-semibold transition-colors',
								on ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
							)}
						>
							{label}
							{on && (
								<span
									aria-hidden="true"
									className="absolute inset-x-10 bottom-0 h-0.5 rounded-full bg-primary"
								/>
							)}
						</button>
					)
				})}
			</div>

			{/* Your rank — the slot exists; the rank does not yet. */}
			<div className="mx-4 mt-4 flex items-center gap-3 rounded-2xl bg-card px-4 py-3.5">
				<GhostAvatar size={44} />
				<div className="min-w-0 flex-1">
					<p className="block text-sm text-muted-foreground">Your rank</p>
					<span className="mt-1 inline-flex items-center rounded-lg bg-primary/15 px-2 py-0.5 text-sm font-bold tabular-nums text-primary">
						#&nbsp;—
					</span>
				</div>
				<span className="shrink-0 text-lg font-bold tabular-nums text-muted-foreground">—</span>
			</div>

			{/* Window pills + unit toggle. Real controls: they will drive the
			    ranking query; today they drive the label below. */}
			<div className="mt-4 flex items-center justify-between px-4">
				<h2 className="text-[15px] font-semibold">Top traders</h2>
				<div className="flex items-center gap-3">
					<div role="group" aria-label="Time window" className="flex items-center gap-1">
						{(['24h', '7d', '30d'] as const).map((w) => (
							<button
								key={w}
								type="button"
								aria-pressed={window_ === w}
								data-testid={`alkanes-lb-window-${w}`}
								onClick={() => setWindow(w)}
								className={cn(
									'rounded-md px-2 py-1 text-xs font-semibold tabular-nums transition-colors',
									window_ === w
										? 'bg-foreground text-background'
										: 'text-muted-foreground hover:text-foreground'
								)}
							>
								{w}
							</button>
						))}
					</div>
					<div
						role="group"
						aria-label="Ranking unit"
						className="flex items-center rounded-lg bg-secondary p-0.5"
					>
						{(['%', '$'] as const).map((u) => (
							<button
								key={u}
								type="button"
								aria-pressed={unit === u}
								data-testid={`alkanes-lb-unit-${u === '%' ? 'pct' : 'usd'}`}
								onClick={() => setUnit(u)}
								className={cn(
									'rounded-md px-2.5 py-1 text-xs font-bold transition-colors',
									unit === u
										? 'bg-foreground text-background'
										: 'text-muted-foreground hover:text-foreground'
								)}
							>
								{u}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* The ranked rows, as explicitly empty frames: medal-or-number,
			    avatar, name and handle widths, the gain column. No invented
			    traders, no invented figures. */}
			<div aria-hidden="true" className="mt-2 opacity-60">
				{[1, 2, 3, 4, 5, 6].map((n) => (
					<div key={n} className="flex items-center gap-3 px-4 py-3">
						<RankMark n={n} />
						<GhostAvatar />
						<div className="min-w-0 flex-1 space-y-1.5">
							<Bar className="w-24" />
							<Bar className="w-16" />
						</div>
						<div className="flex shrink-0 flex-col items-end gap-1.5">
							<Bar className="w-16" />
							<Bar className="w-10" />
						</div>
					</div>
				))}
			</div>

			<div
				data-testid="alkanes-leaderboard-empty"
				className="mx-4 mb-2 mt-1 rounded-2xl bg-card p-5 text-center"
			>
				<Trophy className="mx-auto size-6 text-muted-foreground" aria-hidden="true" />
				<h3 className="mt-2 text-sm font-semibold">
					{tab === 'friends' ? 'Friends aren’t live yet' : 'No rankings yet'}
				</h3>
				<p className="mx-auto mt-1 max-w-[44ch] text-xs leading-relaxed text-muted-foreground">
					{tab === 'friends'
						? 'Following other traders arrives with the social feed. Nothing above is a friend — the rows are layout only.'
						: `No global per-trader PnL exists for alkanes yet — trades are pseudonymous on-chain and nothing ties a fill to a person. The ${window_} ${
								unit === '%' ? 'percentage' : 'dollar'
						  } ranking will fill these rows when a ranking service ships. Nothing above is a trader.`}
					{!loggedIn && ' Connect a wallet to claim your spot when it does.'}
				</p>
			</div>
		</section>
	)
}
